/**
 * DataGrid Adapter for Therapeutic Areas
 * Transforms field registry to MUI DataGrid column definitions
 */

import { GridColDef } from '@mui/x-data-grid';
import { BaseFieldDefinition } from '../../../shared/types/fieldRegistry';
import { createCellRenderer as createSharedCellRenderer } from '../../../shared/utils/cellRenderers';
import { getGridFilterOperatorsForType } from '../../../utils/fieldOperators';
import { THERAPEUTIC_AREAS_FIELDS_REGISTRY } from '../fields';
import { TherapeuticArea } from '../../../services/api/domains/therapeutic-areas/types';

/**
 * Create a cell renderer for a specific field
 * Uses shared renderer with optional domain-specific overrides
 */
function createTherapeuticAreaCellRenderer(
  field: BaseFieldDefinition<TherapeuticArea>
): GridColDef['renderCell'] {
  // For Therapeutic Areas, we use the shared renderer for all fields
  // Add custom overrides here if needed for domain-specific logic
  return createSharedCellRenderer<TherapeuticArea>(field);
}

/**
 * Transform field registry to DataGrid column definitions
 */
export function toDataGridColumns(): GridColDef<TherapeuticArea>[] {
  return THERAPEUTIC_AREAS_FIELDS_REGISTRY.map(field => {
    const column: GridColDef<TherapeuticArea> = {
      field: field.key,
      headerName: field.label,
      sortable: field.isSortable,
      filterable: field.isFilterable,
    };

    // Apply render configuration
    if (field.renderConfig) {
      if (field.renderConfig.width) column.width = field.renderConfig.width;
      if (field.renderConfig.minWidth)
        column.minWidth = field.renderConfig.minWidth;
      if (field.renderConfig.flex) column.flex = field.renderConfig.flex;
    }

    // Apply filter operators with type casting
    if (field.isFilterable) {
      column.filterOperators = getGridFilterOperatorsForType(
        field.dataType as 'string' | 'number' | 'date'
      );
    }

    // Apply custom cell renderer
    column.renderCell = createTherapeuticAreaCellRenderer(field);

    return column;
  });
}

/**
 * Get default column visibility model
 * Hides metadata columns by default
 */
export function getDefaultColumnVisibility(): Record<string, boolean> {
  return {
    user_id: false,
    created_date: false,
    modified_date: false,
  };
}
